import { forwardRef, SVGProps } from "react";

interface IconProps extends SVGProps<SVGSVGElement> {
  size?: number;
}

// Custom XPAND icon style: ultra-lightweight strokes, rounded caps, unique designs
const baseProps = {
  strokeWidth: 1.25,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  fill: "none",
};

export const XpandMic = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Unique microphone with rounded capsule design */}
      <rect x="9" y="2" width="6" height="11" rx="3" />
      <path d="M5 10v1a7 7 0 0 0 14 0v-1" />
      <line x1="12" y1="18" x2="12" y2="22" />
      <path d="M9 22h6" />
      {/* Sound wave accent */}
      <path d="M19 7c1 1 1 3 0 4" opacity="0.5" />
    </svg>
  )
);
XpandMic.displayName = "XpandMic";

export const XpandBrain = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* MotionOS: Abstract neural core with orbital rings */}
      {/* Central core */}
      <circle cx="12" cy="12" r="3" />
      <circle cx="12" cy="12" r="1" fill="currentColor" opacity="0.4" />
      {/* Outer orbital ring */}
      <ellipse cx="12" cy="12" rx="9" ry="4" opacity="0.5" />
      {/* Angled orbital ring */}
      <ellipse cx="12" cy="12" rx="9" ry="4" transform="rotate(60 12 12)" opacity="0.4" />
      {/* Counter orbital ring */}
      <ellipse cx="12" cy="12" rx="9" ry="4" transform="rotate(-60 12 12)" opacity="0.3" />
      {/* Neural nodes on orbits */}
      <circle cx="21" cy="12" r="1.5" fill="currentColor" opacity="0.6" />
      <circle cx="3" cy="12" r="1.5" fill="currentColor" opacity="0.6" />
      <circle cx="16.5" cy="5" r="1" fill="currentColor" opacity="0.4" />
      <circle cx="7.5" cy="19" r="1" fill="currentColor" opacity="0.4" />
    </svg>
  )
);
XpandBrain.displayName = "XpandBrain";

export const XpandUsers = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Unique CRM/users with connected design */}
      <circle cx="9" cy="7" r="3" />
      <circle cx="17" cy="9" r="2.5" opacity="0.7" />
      <path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
      <path d="M17 14.5c1.5 0 3 1 3.5 2.5" opacity="0.7" />
      {/* Connection line */}
      <path d="M12 7h3" strokeDasharray="2 2" opacity="0.4" />
    </svg>
  )
);
XpandUsers.displayName = "XpandUsers";

export const XpandCalendar = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Unique calendar with smart scheduling accent */}
      <rect x="3" y="4" width="18" height="18" rx="3" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="16" y1="2" x2="16" y2="6" />
      {/* Smart dot indicators */}
      <circle cx="8" cy="14" r="1" fill="currentColor" opacity="0.6" />
      <circle cx="12" cy="14" r="1" fill="currentColor" />
      <circle cx="16" cy="14" r="1" fill="currentColor" opacity="0.6" />
      <circle cx="12" cy="18" r="1" fill="currentColor" opacity="0.4" />
    </svg>
  )
);
XpandCalendar.displayName = "XpandCalendar";

export const XpandStore = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Unique marketplace/store with grid accent */}
      <path d="M4 7l1.5-4h13L20 7" />
      <path d="M4 7v13a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V7" />
      <path d="M4 7h16" />
      {/* Grid pattern for marketplace feel */}
      <rect x="7" y="11" width="4" height="4" rx="0.5" opacity="0.7" />
      <rect x="13" y="11" width="4" height="4" rx="0.5" opacity="0.5" />
      <path d="M9 17v4" opacity="0.4" />
      <path d="M15 17v4" opacity="0.4" />
    </svg>
  )
);
XpandStore.displayName = "XpandStore";

export const XpandGift = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Done-for-you gift with ribbon */}
      <rect x="3" y="8" width="18" height="13" rx="2" />
      <path d="M12 8v13" />
      <path d="M3 12h18" opacity="0.6" />
      <path d="M8 8c0-2 1-4 4-4s4 2 4 4" />
      <circle cx="12" cy="5" r="1" fill="currentColor" opacity="0.5" />
    </svg>
  )
);
XpandGift.displayName = "XpandGift";

export const XpandZap = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Easy install lightning with unique style */}
      <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />
      {/* Speed lines */}
      <path d="M18 6l2-1" opacity="0.4" />
      <path d="M19 10h2" opacity="0.3" />
    </svg>
  )
);
XpandZap.displayName = "XpandZap";

export const XpandPlug = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Extensions/plugins with connection design */}
      <path d="M12 2v4" />
      <path d="M12 18v4" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" fill="currentColor" opacity="0.3" />
      {/* Connection points */}
      <circle cx="12" cy="6" r="1" fill="currentColor" />
      <circle cx="12" cy="18" r="1" fill="currentColor" />
      <circle cx="6" cy="12" r="1" fill="currentColor" opacity="0.6" />
      <circle cx="18" cy="12" r="1" fill="currentColor" opacity="0.6" />
    </svg>
  )
);
XpandPlug.displayName = "XpandPlug";

export const XpandBuilding = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Enterprise building with modern design */}
      <rect x="4" y="6" width="16" height="16" rx="1" />
      <path d="M4 10h16" opacity="0.5" />
      <rect x="8" y="2" width="8" height="4" rx="1" />
      {/* Windows pattern */}
      <rect x="7" y="13" width="3" height="3" rx="0.5" opacity="0.6" />
      <rect x="14" y="13" width="3" height="3" rx="0.5" opacity="0.6" />
      <path d="M10 22v-4h4v4" opacity="0.7" />
    </svg>
  )
);
XpandBuilding.displayName = "XpandBuilding";

export const XpandUtensils = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Hospitality: Fork and knife crossed */}
      {/* Fork */}
      <path d="M3 2v7c0 1.1.9 2 2 2h2a2 2 0 0 0 2-2V2" />
      <path d="M6 2v20" />
      <path d="M3 2h6" opacity="0.5" />
      {/* Knife */}
      <path d="M18 2v20" />
      <path d="M18 2c2 2 2 6 0 8" />
      <circle cx="18" cy="14" r="1" fill="currentColor" opacity="0.4" />
    </svg>
  )
);
XpandUtensils.displayName = "XpandUtensils";

export const XpandHeadphones = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Support headphones with unique ear cup design */}
      <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
      <rect x="3" y="14" width="4" height="7" rx="2" />
      <rect x="17" y="14" width="4" height="7" rx="2" />
      {/* Sound wave accent */}
      <path d="M9 11c0-1.5 1.5-3 3-3s3 1.5 3 3" opacity="0.4" />
    </svg>
  )
);
XpandHeadphones.displayName = "XpandHeadphones";

export const XpandTrending = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Sales/trending with dynamic line */}
      <path d="M3 17l6-6 4 4 8-10" />
      <path d="M17 5h4v4" />
      {/* Growth dots */}
      <circle cx="3" cy="17" r="1.5" fill="currentColor" opacity="0.4" />
      <circle cx="9" cy="11" r="1.5" fill="currentColor" opacity="0.6" />
      <circle cx="13" cy="15" r="1.5" fill="currentColor" opacity="0.8" />
      <circle cx="21" cy="5" r="1.5" fill="currentColor" />
    </svg>
  )
);
XpandTrending.displayName = "XpandTrending";

export const XpandMegaphone = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Marketing megaphone with broadcast waves */}
      <path d="M18 8a6 6 0 0 1 0 8" opacity="0.5" />
      <path d="M21 5a10 10 0 0 1 0 14" opacity="0.3" />
      <path d="M4 15l10-3V6L4 9v6z" />
      <path d="M4 9h2v6H4" />
      <path d="M7 18l1-3" opacity="0.7" />
    </svg>
  )
);
XpandMegaphone.displayName = "XpandMegaphone";

export const XpandCode = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Development with unique bracket style */}
      <path d="M8 6L3 12l5 6" />
      <path d="M16 6l5 6-5 6" />
      <path d="M14 4l-4 16" opacity="0.6" />
      {/* Cursor accent */}
      <rect x="10" y="10" width="4" height="4" rx="0.5" fill="currentColor" opacity="0.2" />
    </svg>
  )
);
XpandCode.displayName = "XpandCode";

export const XpandSparkles = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Beauty/wellness sparkles */}
      <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" />
      <path d="M19 13l1 3 3 1-3 1-1 3-1-3-3-1 3-1 1-3z" opacity="0.6" />
      <circle cx="5" cy="17" r="2" opacity="0.4" />
    </svg>
  )
);
XpandSparkles.displayName = "XpandSparkles";

export const XpandBook = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Documentation book */}
      <path d="M4 4h5a3 3 0 0 1 3 3v13c0-.83-.67-1.5-1.5-1.5H4V4z" />
      <path d="M20 4h-5a3 3 0 0 0-3 3v13c0-.83.67-1.5 1.5-1.5H20V4z" />
      {/* Page lines */}
      <path d="M7 8h2" opacity="0.5" />
      <path d="M7 11h2" opacity="0.4" />
      <path d="M15 8h2" opacity="0.5" />
      <path d="M15 11h2" opacity="0.4" />
    </svg>
  )
);
XpandBook.displayName = "XpandBook";

export const XpandShield = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Security shield */}
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" opacity="0.6" />
    </svg>
  )
);
XpandShield.displayName = "XpandShield";

export const XpandRocket = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Launch rocket */}
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
      <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
      <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
      <circle cx="16" cy="8" r="1.5" fill="currentColor" opacity="0.5" />
    </svg>
  )
);
XpandRocket.displayName = "XpandRocket";

export const XpandFileText = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Blog/articles */}
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      {/* Text lines */}
      <path d="M8 13h8" opacity="0.7" />
      <path d="M8 17h6" opacity="0.5" />
      <path d="M8 9h2" opacity="0.4" />
    </svg>
  )
);
XpandFileText.displayName = "XpandFileText";

export const XpandGlobe = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Case studies/global */}
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20" opacity="0.5" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      {/* Connection dots */}
      <circle cx="6" cy="8" r="1" fill="currentColor" opacity="0.4" />
      <circle cx="18" cy="16" r="1" fill="currentColor" opacity="0.4" />
    </svg>
  )
);
XpandGlobe.displayName = "XpandGlobe";

export const XpandArrowRight = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      <path d="M5 12h14" />
      <path d="M13 6l6 6-6 6" />
    </svg>
  )
);
XpandArrowRight.displayName = "XpandArrowRight";

export const XpandChevronDown = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  )
);
XpandChevronDown.displayName = "XpandChevronDown";

export const XpandMenu = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      <path d="M4 6h16" />
      <path d="M4 12h16" />
      <path d="M4 18h16" />
    </svg>
  )
);
XpandMenu.displayName = "XpandMenu";

export const XpandX = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      <path d="M18 6L6 18" />
      <path d="M6 6l12 12" />
    </svg>
  )
);
XpandX.displayName = "XpandX";

export const XpandBarChart = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Analytics/charts */}
      <rect x="4" y="14" width="4" height="7" rx="1" opacity="0.5" />
      <rect x="10" y="9" width="4" height="12" rx="1" opacity="0.7" />
      <rect x="16" y="4" width="4" height="17" rx="1" />
      {/* Trend line */}
      <path d="M4 10l6-4 6 2 4-5" strokeDasharray="2 2" opacity="0.4" />
    </svg>
  )
);
XpandBarChart.displayName = "XpandBarChart";

export const XpandDashboard = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Dashboard grid layout */}
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="4" rx="1" opacity="0.7" />
      <rect x="14" y="10" width="7" height="11" rx="1.5" opacity="0.5" />
      <rect x="3" y="13" width="7" height="8" rx="1.5" opacity="0.6" />
      {/* Activity dot */}
      <circle cx="6.5" cy="6.5" r="1" fill="currentColor" opacity="0.4" />
    </svg>
  )
);
XpandDashboard.displayName = "XpandDashboard";

export const XpandActivity = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Live activity pulse */}
      <path d="M3 12h4l3-9 4 18 3-9h4" />
      {/* Pulse dot */}
      <circle cx="20" cy="12" r="2" fill="currentColor" opacity="0.6" />
      <circle cx="20" cy="12" r="3.5" opacity="0.3" />
    </svg>
  )
);
XpandActivity.displayName = "XpandActivity";

export const XpandPackage = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Product package with 3D feel */}
      <path d="M12 2l9 4.5v11L12 22l-9-4.5v-11L12 2z" />
      <path d="M12 12l9-4.5" opacity="0.5" />
      <path d="M12 12L3 7.5" opacity="0.5" />
      <path d="M12 12v10" opacity="0.6" />
      {/* Package detail */}
      <circle cx="12" cy="8" r="1.5" fill="currentColor" opacity="0.4" />
    </svg>
  )
);
XpandPackage.displayName = "XpandPackage";

export const XpandCart = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Modern cart */}
      <path d="M6 6h15l-1.5 9H7.5L6 6z" />
      <path d="M6 6L5 3H2" opacity="0.6" />
      <circle cx="9" cy="20" r="1.5" fill="currentColor" />
      <circle cx="18" cy="20" r="1.5" fill="currentColor" />
      {/* Cart item */}
      <path d="M10 10h4" opacity="0.4" />
    </svg>
  )
);
XpandCart.displayName = "XpandCart";

export const XpandDollar = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Dollar sign in circle */}
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v12" />
      <path d="M8 9.5c0-1.4 1.8-2.5 4-2.5s4 1.1 4 2.5-1.8 2.5-4 2.5-4 1.1-4 2.5 1.8 2.5 4 2.5 4-1.1 4-2.5" />
    </svg>
  )
);
XpandDollar.displayName = "XpandDollar";

export const XpandWallet = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Wallet */}
      <rect x="2" y="6" width="20" height="14" rx="2" />
      <path d="M2 10h20" opacity="0.5" />
      <circle cx="17" cy="14" r="2" fill="currentColor" opacity="0.6" />
      <path d="M6 6V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2" opacity="0.4" />
    </svg>
  )
);
XpandWallet.displayName = "XpandWallet";

export const XpandMessage = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Message bubble */}
      <path d="M21 12c0 4.4-4 8-9 8-1.6 0-3-.3-4.3-.9L3 21l1.9-4.7C3.7 15 3 13.6 3 12c0-4.4 4-8 9-8s9 3.6 9 8z" />
      {/* Message dots */}
      <circle cx="8" cy="12" r="1" fill="currentColor" opacity="0.6" />
      <circle cx="12" cy="12" r="1" fill="currentColor" opacity="0.8" />
      <circle cx="16" cy="12" r="1" fill="currentColor" opacity="0.6" />
    </svg>
  )
);
XpandMessage.displayName = "XpandMessage";

export const XpandSettings = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Settings gear */}
      <circle cx="12" cy="12" r="3" />
      <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" opacity="0.6" />
    </svg>
  )
);
XpandSettings.displayName = "XpandSettings";

export const XpandTag = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Price tag */}
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
      <circle cx="7" cy="7" r="2" fill="currentColor" opacity="0.5" />
    </svg>
  )
);
XpandTag.displayName = "XpandTag";

export const XpandDownload = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Download arrow */}
      <path d="M12 3v12" />
      <path d="M7 10l5 5 5-5" />
      <path d="M5 17h14" opacity="0.6" />
      <path d="M5 21h14" opacity="0.3" />
    </svg>
  )
);
XpandDownload.displayName = "XpandDownload";

export const XpandStar = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Star rating */}
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  )
);
XpandStar.displayName = "XpandStar";

export const XpandEye = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Eye view */}
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
      <circle cx="12" cy="12" r="1" fill="currentColor" opacity="0.5" />
    </svg>
  )
);
XpandEye.displayName = "XpandEye";

export const XpandHelp = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Help circle */}
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <circle cx="12" cy="17" r="0.5" fill="currentColor" />
    </svg>
  )
);
XpandHelp.displayName = "XpandHelp";

export const XpandPlus = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  )
);
XpandPlus.displayName = "XpandPlus";

export const XpandBack = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      <path d="M19 12H5" />
      <path d="M12 19l-7-7 7-7" />
    </svg>
  )
);
XpandBack.displayName = "XpandBack";

export const XpandReport = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Report document with chart */}
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <path d="M8 10h8" opacity="0.4" />
      <path d="M8 14h4" opacity="0.4" />
      {/* Mini chart */}
      <rect x="7" y="16" width="2" height="3" rx="0.5" fill="currentColor" opacity="0.5" />
      <rect x="11" y="15" width="2" height="4" rx="0.5" fill="currentColor" opacity="0.6" />
      <rect x="15" y="14" width="2" height="5" rx="0.5" fill="currentColor" opacity="0.7" />
    </svg>
  )
);
XpandReport.displayName = "XpandReport";

// Setup Complexity Icons
export const XpandWand = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Magic wand for done-for-you */}
      <path d="M15 4l-1 4-4 1 4 1 1 4 1-4 4-1-4-1-1-4z" />
      <path d="M8 16l-6 6" />
      <path d="M10 14l-8 8" opacity="0.6" />
      <circle cx="15" cy="9" r="1" fill="currentColor" opacity="0.4" />
    </svg>
  )
);
XpandWand.displayName = "XpandWand";

export const XpandPlayCircle = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Plug and play */}
      <circle cx="12" cy="12" r="10" />
      <path d="M10 8l6 4-6 4V8z" fill="currentColor" opacity="0.3" />
    </svg>
  )
);
XpandPlayCircle.displayName = "XpandPlayCircle";

export const XpandClipboard = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Guided setup clipboard */}
      <rect x="6" y="4" width="12" height="17" rx="2" />
      <path d="M10 2h4v4h-4z" opacity="0.6" />
      <path d="M9 10h6" opacity="0.5" />
      <path d="M9 14h4" opacity="0.5" />
      <circle cx="9" cy="10" r="0.5" fill="currentColor" />
      <circle cx="9" cy="14" r="0.5" fill="currentColor" />
    </svg>
  )
);
XpandClipboard.displayName = "XpandClipboard";

export const XpandPalette = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* No-code palette */}
      <circle cx="12" cy="12" r="10" />
      <path d="M12 2a10 10 0 0 0 0 20c1.1 0 2-1 2-2.1 0-.5-.2-1-.5-1.4-.3-.4-.5-.8-.5-1.4 0-1.1.9-2.1 2-2.1h2.4c3 0 5.6-2.5 5.6-5.5C23 5.5 18.2 2 12 2z" />
      <circle cx="8" cy="8" r="1.5" fill="currentColor" opacity="0.6" />
      <circle cx="16" cy="8" r="1.5" fill="currentColor" opacity="0.5" />
      <circle cx="6" cy="13" r="1.5" fill="currentColor" opacity="0.4" />
    </svg>
  )
);
XpandPalette.displayName = "XpandPalette";

export const XpandPuzzle = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Low-code puzzle */}
      <path d="M19 9h-2V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h4v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V11a2 2 0 0 0-2-2z" />
      <path d="M13 9v2h2" opacity="0.5" />
      <circle cx="9" cy="9" r="2" opacity="0.4" />
      <circle cx="15" cy="15" r="2" opacity="0.4" />
    </svg>
  )
);
XpandPuzzle.displayName = "XpandPuzzle";

export const XpandLink = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* API Integration link */}
      <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.5 1.5" />
      <path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.5-1.5" />
    </svg>
  )
);
XpandLink.displayName = "XpandLink";

export const XpandTerminal = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* SDK terminal */}
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M7 10l3 2-3 2" />
      <path d="M12 14h5" opacity="0.5" />
    </svg>
  )
);
XpandTerminal.displayName = "XpandTerminal";

export const XpandWrench = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Custom deployment wrench */}
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.8-3.8a6 6 0 0 1-7.8 7.8l-6.6 6.6a2 2 0 0 1-2.8-2.8l6.6-6.6a6 6 0 0 1 7.8-7.8l-4 4z" />
    </svg>
  )
);
XpandWrench.displayName = "XpandWrench";

// Delivery Method Icons
export const XpandCloud = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Hosted SaaS cloud */}
      <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
      <circle cx="12" cy="13" r="1" fill="currentColor" opacity="0.4" />
    </svg>
  )
);
XpandCloud.displayName = "XpandCloud";

export const XpandWidget = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Website widget */}
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18" opacity="0.4" />
      <rect x="14" y="12" width="5" height="7" rx="1" opacity="0.6" />
      <path d="M6 6h2" opacity="0.3" />
    </svg>
  )
);
XpandWidget.displayName = "XpandWidget";

export const XpandExtension = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Chrome extension puzzle */}
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <path d="M15 3h6v6" />
      <path d="M10 14L21 3" />
    </svg>
  )
);
XpandExtension.displayName = "XpandExtension";

export const XpandApi = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* API access */}
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M9 9l6 6" />
      <path d="M15 9l-6 6" />
      <circle cx="12" cy="12" r="3" opacity="0.3" />
    </svg>
  )
);
XpandApi.displayName = "XpandApi";

export const XpandBolt = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Zapier bolt */}
      <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />
    </svg>
  )
);
XpandBolt.displayName = "XpandBolt";

export const XpandSlack = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Slack / Teams app grid */}
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" opacity="0.7" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" opacity="0.7" />
    </svg>
  )
);
XpandSlack.displayName = "XpandSlack";

export const XpandWhatsApp = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* WhatsApp phone */}
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  )
);
XpandWhatsApp.displayName = "XpandWhatsApp";

export const XpandFileDownload = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* File download */}
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M12 11v6" />
      <path d="M9 14l3 3 3-3" />
    </svg>
  )
);
XpandFileDownload.displayName = "XpandFileDownload";

export const XpandSdk = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* SDK library books */}
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      <path d="M8 7h8" opacity="0.5" />
      <path d="M8 11h6" opacity="0.4" />
    </svg>
  )
);
XpandSdk.displayName = "XpandSdk";

export const XpandDocker = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Docker container */}
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M6 7V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2" />
      <rect x="6" y="11" width="3" height="3" rx="0.5" opacity="0.5" />
      <rect x="10.5" y="11" width="3" height="3" rx="0.5" opacity="0.6" />
      <rect x="15" y="11" width="3" height="3" rx="0.5" opacity="0.5" />
    </svg>
  )
);
XpandDocker.displayName = "XpandDocker";

export const XpandCustom = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Custom integration cog */}
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  )
);
XpandCustom.displayName = "XpandCustom";

export const XpandMcp = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* MCP Server AI */}
      <circle cx="12" cy="12" r="3" />
      <circle cx="12" cy="12" r="8" opacity="0.4" />
      <path d="M12 4v2" />
      <path d="M12 18v2" />
      <path d="M4 12h2" />
      <path d="M18 12h2" />
      <circle cx="12" cy="12" r="1" fill="currentColor" opacity="0.5" />
    </svg>
  )
);
XpandMcp.displayName = "XpandMcp";

// Pricing Model Icons
export const XpandFree = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Free - Gift/Tag with zero */}
      <rect x="3" y="8" width="18" height="13" rx="2" />
      <path d="M12 8V2" opacity="0.6" />
      <path d="M7 2l5 6 5-6" opacity="0.5" />
      <path d="M3 12h18" opacity="0.3" />
      <circle cx="12" cy="15" r="2" opacity="0.6" />
    </svg>
  )
);
XpandFree.displayName = "XpandFree";

export const XpandOneTime = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* One-time - Single payment/coin */}
      <circle cx="12" cy="12" r="9" />
      <path d="M12 6v12" opacity="0.6" />
      <path d="M9 9h4.5a1.5 1.5 0 0 1 0 3H10" />
      <path d="M10 12h3.5a1.5 1.5 0 0 1 0 3H9" />
    </svg>
  )
);
XpandOneTime.displayName = "XpandOneTime";

export const XpandSubscription = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Subscription - Recurring/refresh with calendar */}
      <path d="M21 12a9 9 0 1 1-9-9" />
      <path d="M21 3v9h-9" />
      <circle cx="12" cy="12" r="3" opacity="0.5" />
      <circle cx="12" cy="12" r="1" fill="currentColor" opacity="0.4" />
    </svg>
  )
);
XpandSubscription.displayName = "XpandSubscription";

export const XpandUsageBased = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Usage-based - Meter/chart */}
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M7 17V13" />
      <path d="M11 17V9" />
      <path d="M15 17V11" />
      <path d="M19 17V7" opacity="0.6" />
      <path d="M3 13h4" opacity="0.3" />
    </svg>
  )
);
XpandUsageBased.displayName = "XpandUsageBased";

// Additional landing page icons
export const XpandPhone = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Phone with unique call wave design */}
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
      {/* Call wave accents */}
      <path d="M14.5 2c3 .5 5 2.5 5.5 5.5" opacity="0.5" />
      <path d="M14.5 5.5c1.5.3 2.5 1.3 2.8 2.8" opacity="0.3" />
    </svg>
  )
);
XpandPhone.displayName = "XpandPhone";

export const XpandClock = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Time/clock with elegant design */}
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
      {/* Decorative tick marks */}
      <line x1="12" y1="2" x2="12" y2="4" opacity="0.4" />
      <line x1="22" y1="12" x2="20" y2="12" opacity="0.4" />
      <line x1="12" y1="20" x2="12" y2="22" opacity="0.4" />
      <line x1="4" y1="12" x2="2" y2="12" opacity="0.4" />
    </svg>
  )
);
XpandClock.displayName = "XpandClock";

export const XpandHeart = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Heart with elegant curves */}
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      {/* Decorative pulse accent */}
      <path d="M12 13l-2-2 4-4" opacity="0.3" />
    </svg>
  )
);
XpandHeart.displayName = "XpandHeart";

export const XpandRefresh = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Sync/refresh with flow design */}
      <path d="M21 2v6h-6" />
      <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
      <path d="M3 22v-6h6" />
      <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
      {/* Center dot */}
      <circle cx="12" cy="12" r="1.5" fill="currentColor" opacity="0.4" />
    </svg>
  )
);
XpandRefresh.displayName = "XpandRefresh";

export const XpandCheck = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Check with circle */}
      <circle cx="12" cy="12" r="10" opacity="0.3" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  )
);
XpandCheck.displayName = "XpandCheck";

export const XpandUser = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Single user with elegant design */}
      <circle cx="12" cy="8" r="4" />
      <path d="M20 21a8 8 0 1 0-16 0" />
      {/* Decorative accent */}
      <circle cx="12" cy="8" r="1.5" fill="currentColor" opacity="0.3" />
    </svg>
  )
);
XpandUser.displayName = "XpandUser";

// Additional landing page icons
export const XpandQuote = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Elegant quotation mark */}
      <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V21z" opacity="0.7" />
      <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v4z" opacity="0.5" />
    </svg>
  )
);
XpandQuote.displayName = "XpandQuote";

export const XpandScissors = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Elegant scissors */}
      <circle cx="6" cy="6" r="3" />
      <circle cx="6" cy="18" r="3" />
      <path d="M20 4L8.12 15.88" />
      <path d="M14.47 14.48L20 20" />
      <path d="M8.12 8.12L12 12" opacity="0.5" />
    </svg>
  )
);
XpandScissors.displayName = "XpandScissors";

export const XpandStethoscope = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Medical stethoscope */}
      <path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3" />
      <path d="M8 15v1a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6v-4" opacity="0.7" />
      <circle cx="20" cy="10" r="2" />
      {/* Accent */}
      <circle cx="8" cy="9" r="1" fill="currentColor" opacity="0.3" />
    </svg>
  )
);
XpandStethoscope.displayName = "XpandStethoscope";

export const XpandCar = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Sleek car design */}
      <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2" />
      <circle cx="7" cy="17" r="2" />
      <circle cx="17" cy="17" r="2" />
      {/* Window */}
      <path d="M10 10l-2-3h4" opacity="0.4" />
    </svg>
  )
);
XpandCar.displayName = "XpandCar";

export const XpandHome = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Modern home */}
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" opacity="0.6" />
      {/* Chimney accent */}
      <path d="M16 5v2" opacity="0.4" />
    </svg>
  )
);
XpandHome.displayName = "XpandHome";

export const XpandDumbbell = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Fitness dumbbell */}
      <path d="M6.5 6.5a2.12 2.12 0 0 1 3 3L7 12l2.5 2.5a2.12 2.12 0 1 1-3 3L4 15l-2.5 2.5a2.12 2.12 0 0 1-3-3L1 12 -1.5 9.5a2.12 2.12 0 0 1 3-3L4 9z" transform="translate(6 0)" />
      <path d="M7 12h10" />
      <path d="M22 12l-4-4v8l4-4z" opacity="0.5" />
      <path d="M2 12l4 4V8L2 12z" opacity="0.5" />
    </svg>
  )
);
XpandDumbbell.displayName = "XpandDumbbell";

export const XpandBriefcase = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Professional briefcase */}
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
      {/* Clasp accent */}
      <path d="M12 11v2" opacity="0.5" />
      <circle cx="12" cy="11" r="1" fill="currentColor" opacity="0.3" />
    </svg>
  )
);
XpandBriefcase.displayName = "XpandBriefcase";

export const XpandPlane = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Travel airplane */}
      <path d="M17.8 19.2L16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.4-.1.9.3 1.1L11 12l-2 3H6l-2 2 4-1 1-4 3-2 3.5 7.3c.2.4.7.6 1.1.4l.5-.3c.4-.2.6-.6.5-1.1z" />
      {/* Trail accent */}
      <path d="M4 17l1-1" opacity="0.4" />
    </svg>
  )
);
XpandPlane.displayName = "XpandPlane";

export const XpandHotel = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      stroke="currentColor"
      {...baseProps}
      {...props}
    >
      {/* Hotel building */}
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18" opacity="0.5" />
      {/* Windows */}
      <rect x="6" y="12" width="3" height="3" rx="0.5" opacity="0.6" />
      <rect x="10.5" y="12" width="3" height="3" rx="0.5" opacity="0.6" />
      <rect x="15" y="12" width="3" height="3" rx="0.5" opacity="0.6" />
      {/* Door */}
      <path d="M10 21v-4h4v4" />
      {/* Sign */}
      <circle cx="12" cy="6" r="1.5" fill="currentColor" opacity="0.4" />
    </svg>
  )
);
XpandHotel.displayName = "XpandHotel";

// Export all icons as a collection
export const XpandIcons = {
  Mic: XpandMic,
  Brain: XpandBrain,
  Users: XpandUsers,
  Calendar: XpandCalendar,
  Store: XpandStore,
  Gift: XpandGift,
  Zap: XpandZap,
  Plug: XpandPlug,
  Building: XpandBuilding,
  Headphones: XpandHeadphones,
  Trending: XpandTrending,
  Megaphone: XpandMegaphone,
  Code: XpandCode,
  Sparkles: XpandSparkles,
  Book: XpandBook,
  FileText: XpandFileText,
  Globe: XpandGlobe,
  ArrowRight: XpandArrowRight,
  ChevronDown: XpandChevronDown,
  Menu: XpandMenu,
  X: XpandX,
  BarChart: XpandBarChart,
  Dashboard: XpandDashboard,
  Activity: XpandActivity,
  Package: XpandPackage,
  Cart: XpandCart,
  Dollar: XpandDollar,
  Wallet: XpandWallet,
  Message: XpandMessage,
  Settings: XpandSettings,
  Tag: XpandTag,
  Download: XpandDownload,
  Star: XpandStar,
  Eye: XpandEye,
  Help: XpandHelp,
  Shield: XpandShield,
  Rocket: XpandRocket,
  Plus: XpandPlus,
  Back: XpandBack,
  Report: XpandReport,
  // Setup Complexity
  Wand: XpandWand,
  PlayCircle: XpandPlayCircle,
  Clipboard: XpandClipboard,
  Palette: XpandPalette,
  Puzzle: XpandPuzzle,
  Link: XpandLink,
  Terminal: XpandTerminal,
  Wrench: XpandWrench,
  // Delivery Method
  Cloud: XpandCloud,
  Widget: XpandWidget,
  Extension: XpandExtension,
  Api: XpandApi,
  Bolt: XpandBolt,
  Slack: XpandSlack,
  WhatsApp: XpandWhatsApp,
  FileDownload: XpandFileDownload,
  Sdk: XpandSdk,
  Docker: XpandDocker,
  Custom: XpandCustom,
  Mcp: XpandMcp,
  // Pricing Model
  Free: XpandFree,
  OneTime: XpandOneTime,
  Subscription: XpandSubscription,
  UsageBased: XpandUsageBased,
  // Landing page icons
  Phone: XpandPhone,
  Clock: XpandClock,
  Heart: XpandHeart,
  Refresh: XpandRefresh,
  Check: XpandCheck,
  User: XpandUser,
  Quote: XpandQuote,
  Scissors: XpandScissors,
  Stethoscope: XpandStethoscope,
  Car: XpandCar,
  Home: XpandHome,
  Dumbbell: XpandDumbbell,
  Briefcase: XpandBriefcase,
  Plane: XpandPlane,
  Hotel: XpandHotel,
};
